"use client";

import { GeneralCross } from "@fanduel/formation-react-icons";
import ReactModal from "react-modal";
import ErrorComponent from "@/components/empty/ErrorComponent";
import { Card, CardHeader } from "@/components/headToHead/Card";
import Modal from "@/components/headToHead/modal";
import Table from "@/components/headToHead/Table";
import LoadingBackdrop from "@/components/loadingBackdrop/LoadingBackdrop";
import { useGameLogAndStats } from "@/utils/hooks/useGetHeadToHeadPlayerGameLogAndStats";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import { usePlayerStatsContext } from "@/utils/hooks/useHeadToHeadPlayerStatsContext";
import usePlayerLogAndStatsTable from "./usePlayerLogAndStatsTable";
import styles from "./playerModal.module.scss";

type PlayerModalProps = {
  isOpen: boolean;
  toggleModal: () => void;
  playerName: string;
  numberFirePlayerId: number;
  handleRefetch: () => void;
};

type ContentProps = {
  numberFirePlayerId: number;
  handleRefetch: () => void;
};

const Content = ({ numberFirePlayerId, handleRefetch }: ContentProps) => {
  const { gameId } = useHeadToHeadContext();
  const { position: selectedPosition } = usePlayerStatsContext();
  const { gameLogData, gameStatsData, isLoading, isError } = useGameLogAndStats(
    gameId,
    numberFirePlayerId
  );
  const { tableData, columns } = usePlayerLogAndStatsTable(
    gameLogData,
    gameStatsData
  );
  const selectedColumns = columns[selectedPosition];
  const isNoData = !gameLogData || !gameStatsData;

  if (isError && isNoData) {
    return <ErrorComponent ctaButtonOnClick={handleRefetch} />;
  } else if (isLoading && isNoData) {
    return <LoadingBackdrop loading={isLoading} />;
  }

  return <Table data={tableData} columns={selectedColumns} />;
};

if (process.env.NODE_ENV !== "test") {
  ReactModal.setAppElement("#root");
}

const PlayerModal = ({
  isOpen,
  toggleModal,
  playerName,
  numberFirePlayerId,
  handleRefetch,
}: PlayerModalProps) => {
  return (
    <Modal
      contentLabel="Player Game Log"
      isOpen={isOpen}
      closeTimeoutMS={150}
      onRequestClose={toggleModal}
    >
      <Card className={styles.card}>
        <div className={styles.modalCardHeader}>
          <CardHeader title={`${playerName} Game Log`} />
          <button onClick={toggleModal} className={styles.closeButton}>
            <GeneralCross
              data-testid="mobile-cross"
              height={12}
              width={12}
              color="coreGrey"
            />
          </button>
        </div>
        <div className={styles.content}>
          <Content
            numberFirePlayerId={numberFirePlayerId}
            handleRefetch={handleRefetch}
          />
        </div>
      </Card>
    </Modal>
  );
};

export default PlayerModal;
